import React, { useMemo } from "react";
import { Button, Card, CardBody, Input, Label } from "reactstrap";
import ReactTable from "@dhn/components/molecules/data-table";
import SectionHeader from "@dhn/components/molecules/section-header";
import { formatFileSize } from "@dhn/shared/utils/file-size";
import StatusBadge from "@dhn/components/molecules/status-badge";

export const ASSOCIATED_DOCTOR_REQUIREMENTS = [
  { key: "identityCopy", label: "Copia de Cédula" },
  { key: "generalMedicalDegree", label: "Título Médico General" },
  { key: "exequaturCopy", label: "Exequátur" },
];

const ACCEPTED_DOCUMENT_TYPES =
  ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

export default function RequiredDocumentsTable({
  documents = [],
  onFileChange,
  onRemoveDocument,
}) {
  const completedCount = ASSOCIATED_DOCTOR_REQUIREMENTS.filter((requirement) =>
    documents.some((document) => document.requirementKey === requirement.key),
  ).length;
  const missingCount = ASSOCIATED_DOCTOR_REQUIREMENTS.length - completedCount;

  const columns = useMemo(
    () => [
      {
        Header: "Documento requerido",
        accessor: "label",
      },
      {
        Header: "Archivo",
        Cell: ({ original }) => {
          const document = documents.find(
            (item) => item.requirementKey === original.key,
          );
          return document ? (
            <div style={{ minWidth: 0 }}>
              <div className="font-weight-bold text-truncate">
                <i className="fa fa-file-text-o text-info mr-2" />
                {document.name}
              </div>
              <small className="text-muted">
                {formatFileSize(document.size)}
              </small>
            </div>
          ) : (
            <span className="text-muted">
              <i className="fa fa-paperclip mr-1" />
              Sin archivo adjunto
            </span>
          );
        },
      },
      {
        Header: "Estado",
        width: 120,
        Cell: ({ original }) =>
          documents.some(
            (document) => document.requirementKey === original.key,
          ) ? (
            <StatusBadge color="success" icon="fa-check">
              Completo
            </StatusBadge>
          ) : (
            <StatusBadge color="warning" icon="fa-exclamation-circle">
              Pendiente
            </StatusBadge>
          ),
      },
      {
        Header: "Acciones",
        width: 270,
        Cell: ({ original }) => {
          const document = documents.find(
            (item) => item.requirementKey === original.key,
          );
          const inputId = `associated-doctor-document-${original.key}`;
          return (
            <div className="d-flex align-items-center">
              <Label
                for={inputId}
                className="btn btn-primary btn-sm mb-0 mr-2"
              >
                <i className="fa fa-upload mr-1" />
                {document ? "Reemplazar" : "Adjuntar"}
              </Label>
              <Input
                id={inputId}
                type="file"
                accept={ACCEPTED_DOCUMENT_TYPES}
                className="d-none"
                onChange={(event) => onFileChange(original.key, event)}
              />
              {document && (
                <Button
                  type="button"
                  color="danger"
                  size="sm"
                  className="btn-color-danger ml-2"
                  title="Borrar documento"
                  onClick={() => onRemoveDocument(document.id)}
                >
                  <i className="fa fa-times" aria-hidden="true" />
                  {" Borrar"}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [documents, onFileChange, onRemoveDocument],
  );

  return (
    <Card className="mb-0">
      <CardBody>
        <SectionHeader
          compact
          icon="fa-folder-open-o"
          title="Documentos del médico"
          description="Adjunte un archivo por cada documento requerido."
          actions={
            <StatusBadge
              color={missingCount ? "warning" : "success"}
            >
              {completedCount} de {ASSOCIATED_DOCTOR_REQUIREMENTS.length} completos
            </StatusBadge>
          }
        />

        <ReactTable
          className="-striped -highlight"
          data={ASSOCIATED_DOCTOR_REQUIREMENTS}
          columns={columns}
          defaultPageSize={ASSOCIATED_DOCTOR_REQUIREMENTS.length}
          minRows={ASSOCIATED_DOCTOR_REQUIREMENTS.length}
          showPagination={false}
        />

        {missingCount > 0 && (
          <div className="alert alert-warning d-flex align-items-center mb-0 mt-3 py-2">
            <i className="fa fa-exclamation-triangle mr-2" />
            Complete todos los documentos para procesar la solicitud.
          </div>
        )}
        <small className="text-muted d-block mt-2">
          Formatos permitidos: PDF, JPG o PNG. Máximo 8 MB en total, incluyendo
          las evidencias de especialidades.
        </small>
      </CardBody>
    </Card>
  );
}
